import React, {useState, useCallback, useEffect} from 'react';
import LineParameters from './LineParameters.js';
import { Text, Circle, Group, Stage, Layer, Rect, Line} from 'react-konva';
import {getClosestPointIfAny} from './getClosestPointIfAny.js';
import DrawnLine from './DrawnLine.js';
import GuideLines from './GuideLines.js';
import '../../Draw.scss';

const HEIGHT = 400;
const WIDTH = 400;
const PENCIL = 'PENCIL';
const LINE = 'LINE';

export default function DrawComponent(props) {
    const [drawMode, setDrawMode] = useState(PENCIL);
    const [currentLine, setCurrentLine] = useState(undefined);
    const [selectedLine, setSelectedLine] = useState(undefined);
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [lines, setLines] = useState([]);
    const [option, setOption] = useState(LINE);
    const [points, setPoints] = useState([]);

    const onClickOption = useCallback((o) => {
        setOption(o);
    }, [setOption]);

    const onMouseMove = useCallback((e) => {
        if (isMouseDown) {
            if (option === LINE) {
                let {x, y} = getClosestPointIfAny(lines, e.evt.layerX, e.evt.layerY);
                let newLine = {
                    ... currentLine,
                    x2: x,
                    y2: y
                };
                setCurrentLine(newLine);
            } else {
                let x = e.evt.layerX;
                let y = e.evt.layerY;
                setPoints([... points, {x, y}]);
            }
        }
    }, [isMouseDown, setPoints, points, option, currentLine, setCurrentLine, lines]);

    const onLinesChanged = (newLines) => {
        props.onLinesChanged(newLines.map(
            ({x1, y1, x2, y2, brightness}) =>
                ({
                    x1: x1 / WIDTH,
                    y1: y1 / HEIGHT,
                    x2: x2 / WIDTH,
                    y2: y2 / HEIGHT,
                    brightness
                })));
    };

    const onMouseUp = useCallback((e) => {
        if (currentLine) {
            let newLines = [... lines, currentLine];
            setLines(newLines);
            onLinesChanged(newLines);
       }
        setIsMouseDown(false);
        setCurrentLine(undefined);
    }, [setLines, lines, setIsMouseDown, currentLine, setCurrentLine]);

    const onClear = useCallback((e) => {
        setLines([]);
        props.onLinesChanged([]);
    }, [setLines]);

    const onDelete = useCallback((e) => {
        if (selectedLine === undefined) {
            return;
        }

        let i = lines.indexOf(selectedLine);
        lines.splice(i, 1);
        setLines(lines);
        setSelectedLine(undefined);
        onLinesChanged(lines);
    }, [setLines, selectedLine, setSelectedLine]);

    const onMouseDown = useCallback((e) => {
        if (selectedLine) {
            setSelectedLine(undefined);
            return;
        }
        let {x, y} = getClosestPointIfAny(lines, e.evt.layerX, e.evt.layerY);

        setCurrentLine({
            x1: x,
            y1: y,
            x2: x,
            y2: y
        });
        setIsMouseDown(true);
    }, [setIsMouseDown, setCurrentLine, lines, setSelectedLine, selectedLine]);

    let onClickLine = useCallback((line) => {
        if (line === selectedLine) {
            setSelectedLine(undefined);
        } else {
            setSelectedLine(line);
        }
    }, [setSelectedLine, selectedLine]);

    let onChangeLineValue = useCallback((valueChange) => {
        let newLines = [... lines];
        let i = newLines.indexOf(selectedLine);
        newLines[i] = {
            ... selectedLine,
            ... valueChange
        };
        setLines(newLines);
        setSelectedLine(newLines[i]);
        onLinesChanged(newLines);
    }, [setSelectedLine, lines, selectedLine, setLines]);

    let onClickPoint = useCallback((line, x, y) => {
        let currentLine = {... line};
        if (x === line.x1 && y === line.y1) {
            currentLine = {
                x1: currentLine.x2,
                y1: currentLine.y2,
                x2: currentLine.x1,
                y2: currentLine.y1,
            };
        }
        let i = lines.indexOf(line);
        lines.splice(i, 1);
        setLines(lines);
        setCurrentLine(currentLine);
        setIsMouseDown(true);
    }, [setLines, setCurrentLine, lines, setIsMouseDown]);

    useEffect(() => {
        setLines(props.lines.map(
            ({x1, y1, x2, y2, brightness}) => ({
                x1: WIDTH * x1,
                y1: HEIGHT * y1,
                x2: WIDTH * x2,
                y2: HEIGHT * y2,
                brightness
        })));
    }, [props.lines, setLines]);

    return (
        <div className="draw-component">
          <Stage
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseLeave={() => setIsMouseDown(false)}
            onMouseUp={onMouseUp}
            width={WIDTH}
            height={HEIGHT}>
            <Layer>
              <Rect x={0} y={0} width={WIDTH} height={HEIGHT} fill='black'/>
              <GuideLines width={WIDTH} height={HEIGHT}/>
              {points.map(
                  pt =>
                      <Circle x={pt.x} y={pt.y} radius={5} fill="red"/>
              )}
            <CurrentLine currentLine={currentLine}/>
            {lines.map(line =>
                       <DrawnLine
                       onClickPoint={(x,y) => onClickPoint(line, x, y)}
                       isSelected={selectedLine === line}
                       onClickLine={() => onClickLine(line)}
                       line={line}/>)}
            </Layer>
          </Stage>
          <div className="draw-toolbar">
            <DrawOption name="Line" isSelected={option === LINE} onClick={() => onClickOption(LINE)}/>
            <DrawOption name="Pencil" isSelected={option === PENCIL} onClick={() => onClickOption(PENCIL)}/>
            <div className="option" onClick={onClear}>Clear</div>
            <div className="option" onClick={onDelete}>Delete</div>
          </div>
            <LineParameters line={selectedLine} onChangeValue={onChangeLineValue}/>
        </div>
    );
}

function DrawOption(props) {
    let className = "option";
    if (props.isSelected) {
        className += " selected";
    }
    return (
        <div onClick={props.onClick} className={className}>
          {props.name}
        </div>
    );
}

function CurrentLine(props) {
    if (props.currentLine) {
        let {currentLine} = props;
        return (
            <Line
              strokeWidth={2}
              stroke='red'
              points={[currentLine.x1, currentLine.y1, currentLine.x2, currentLine.y2]}/>
        );
    } else {
        return (
            <Circle x={0} y={0} radius={1} fill='black'/>
        );
    }
}
